import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import {
    EMPTY_RECIPIENT,
    TestRecipientFields,
    TestSendStatus,
    TestVariableFields,
} from '../../components/TestSendFields'
import {
    buildTestPayload,
    testRecipient,
    variablesToAsk,
} from '../../helpers/testSend'
import useTestSend from '../../hooks/useTestSend'
import TemplateService from '../../services/templateService'
import { nowUnix } from '../../utils/datetime'
import { RADIUS, SURFACE, TEXT } from '../../theme/tokens'
import { channelMeta } from './wizardShared'

/** "an" before a channel name that starts with a vowel sound ("an Email test"), else "a". */
export const testArticle = (label) =>
    /^[aeiou]/i.test(String(label ?? '')) || /^SMS/i.test(String(label ?? ''))
        ? 'an'
        : 'a'

/**
 * Send a test of one channel of the pipeline being built, before it is
 * launched: the channel's template to an address typed here, as the
 * pipeline's receiver type, with test values for the template's variables,
 * and its delivery outcome followed as on the Test template page (one hook).
 *
 * Optional: Launch does not wait for it. The old wizard required every
 * channel to be marked "Received" on create, a box the user ticked
 * themselves, with no status check behind it.
 *
 * The variables come from the template row (GET /comms/v2/template/{id}:
 * subject, body and declared `others`); until it loads, or if it cannot, the
 * template from the list (`template`) is used. A WhatsApp test goes out from
 * the template's own account (`channel`).
 */
const TestSendPanel = ({ comm, template, receiverType }) => {
    const dispatch = useDispatch()
    const [row, setRow] = useState(null)
    const [recipientValues, setRecipientValues] = useState(EMPTY_RECIPIENT)
    const [variableValues, setVariableValues] = useState({})
    const { sendState, statusMessage, busy, send } = useTestSend()
    const meta = channelMeta(comm.comm_type)

    useEffect(() => {
        setRow(null)
        if (!comm.template_id) return undefined
        let current = true
        dispatch(TemplateService.getTemplate(comm.template_id)).then((resp) => {
            if (current && !resp.error)
                setRow(resp.payload?.data?.template ?? null)
        })
        return () => {
            current = false
        }
    }, [comm.template_id, dispatch])

    const source = row ?? template ?? {}
    const variables = variablesToAsk({
        commType: comm.comm_type,
        texts: [source.subject, source.extracted_body, source.template_data],
        declared: source.others,
    })
    const missing = variables.filter(
        (v) => !String(variableValues[v] ?? '').trim()
    )
    const recipient = testRecipient(comm.comm_type, recipientValues)
    const canSend =
        !busy && !!comm.template_id && !missing.length && recipient.ready

    const onSend = () =>
        send(
            buildTestPayload({
                templateId: comm.template_id,
                commType: comm.comm_type,
                name: recipientValues.name,
                email: recipientValues.email,
                ccs: recipient.ccs,
                mobile: recipient.mobile,
                receiver: recipient.receiver,
                others: Object.fromEntries(
                    variables.map((v) => [v, variableValues[v] ?? ''])
                ),
                eventId: uuidv4(),
                eventTime: nowUnix(),
                receiverType,
                channel:
                    comm.comm_type === 'whatsapp'
                        ? source.channel ?? template?.channel ?? ''
                        : '',
            })
        )

    return (
        <Box
            role="group"
            aria-label={`Test ${meta.label}`}
            sx={{
                p: 2,
                borderRadius: `${RADIUS.md}px`,
                border: `1px solid ${SURFACE.border}`,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                    color: meta.color,
                }}
            >
                {meta.icon}
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {meta.label}
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color: TEXT.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {template?.template_name ?? ''}
                </Typography>
            </Box>
            <Grid container spacing={1.5}>
                <TestRecipientFields
                    commType={comm.comm_type}
                    values={recipientValues}
                    onChange={(patch) =>
                        setRecipientValues((prev) => ({ ...prev, ...patch }))
                    }
                    itemProps={{ xs: 12, sm: 6 }}
                />
            </Grid>
            {variables.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                    <TestVariableFields
                        variables={variables}
                        values={variableValues}
                        onChange={setVariableValues}
                    />
                </Box>
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    mt: 1.5,
                }}
            >
                <Button variant="outlined" onClick={onSend} disabled={!canSend}>
                    {`Send ${testArticle(meta.label)} ${meta.label} test`}
                </Button>
                <TestSendStatus
                    sendState={sendState}
                    statusMessage={statusMessage}
                    busy={busy}
                />
            </Box>
        </Box>
    )
}

TestSendPanel.propTypes = {
    // The channel's comm in the wizard form: comm_type, template_id, channel.
    comm: PropTypes.shape({
        comm_type: PropTypes.string.isRequired,
        template_id: PropTypes.string,
        channel: PropTypes.string,
    }).isRequired,
    // The assigned template from the template list, if it is there.
    template: PropTypes.object,
    // The pipeline's receiver type, sent as the test's receiver_type.
    receiverType: PropTypes.string,
}

export default TestSendPanel
